<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import type { Ref } from 'vue';
import CollapsibleSection from './CollapsibleSection.vue';

interface TaxRate {
  id: string;
  name: string;
  rate: number;
  country: string;
}

interface SelectOption {
  value: string;
  label: string;
}

const { t } = useI18n({ useScope: 'global' });

defineProps<{
  price: Ref<string>;
  productType: Ref<string>;
  priceUnit: Ref<string>;
  packageQty: Ref<string>;
  packageUnit: Ref<string>;
  tvaId: Ref<string>;
  tvaDropdownOpen: Ref<boolean>;
  tvaSearch: Ref<string>;
  taxRates: TaxRate[];
  loadingTaxRates: boolean;
  filteredTaxRates: TaxRate[];
  selectedTaxRate: TaxRate | undefined;
  showPriceError: boolean;
  PRICE_UNIT_OPTIONS: SelectOption[];
  PACKAGE_UNIT_OPTIONS: SelectOption[];
  PRODUCT_TYPES: SelectOption[];
  selectTvaId: (id: string) => void;
}>();
</script>

<template>
  <CollapsibleSection
    section-key="pricing"
    :title="t('product.priceAndType')"
    :subtitle="t('product.basePriceSubtitle')"
    icon="pi-euro"
    :default-open="true"
  >
    <div class="epp__fields">
      <div class="epp__field-row epp__field-row--2">
        <div class="epp__field">
          <label class="epp__label"
            >{{ t('product.price') }} <span class="epp__req">*</span></label
          >
          <div class="epp__price-wrap">
            <input
              v-model="price.value"
              class="epp__input epp__input--price"
              :class="{ 'epp__input--error': showPriceError }"
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
            />
            <span class="epp__price-unit">€</span>
          </div>
          <p v-if="showPriceError" class="epp__field-error">
            {{ t('product.requiredField') }}
          </p>
        </div>
        <div class="epp__field">
          <label class="epp__label">{{ t('product.priceUnit') }}</label>
          <div class="epp__select-wrap">
            <select v-model="priceUnit.value" class="epp__select">
              <option
                v-for="o in PRICE_UNIT_OPTIONS"
                :key="o.value"
                :value="o.value"
              >
                {{ o.label }}
              </option>
            </select>
            <i class="pi pi-chevron-down epp__select-icon" />
          </div>
        </div>
      </div>
      <div class="epp__field-row epp__field-row--2">
        <div class="epp__field">
          <label class="epp__label">{{ t('product.soldByLabel') }}</label>
          <div class="epp__select-wrap">
            <select v-model="packageUnit.value" class="epp__select">
              <option
                v-for="o in PACKAGE_UNIT_OPTIONS"
                :key="o.value"
                :value="o.value"
              >
                {{ o.label }}
              </option>
            </select>
            <i class="pi pi-chevron-down epp__select-icon" />
          </div>
        </div>
        <div class="epp__field">
          <label class="epp__label">{{ t('product.qtyPerFormat') }}</label>
          <input
            v-model="packageQty.value"
            class="epp__input"
            type="number"
            min="1"
            placeholder="ex: 6"
          />
        </div>
      </div>
      <div class="epp__field">
        <label class="epp__label">{{ t('product.productType') }}</label>
        <div class="epp__type-chips">
          <button
            v-for="pt in PRODUCT_TYPES"
            :key="pt.value"
            type="button"
            class="epp__type-chip"
            :class="{
              'epp__type-chip--active': productType.value === pt.value,
            }"
            @click="productType.value = pt.value"
          >
            {{ pt.label }}
          </button>
        </div>
      </div>

      <!-- TVA -->
      <div class="epp__field">
        <label class="epp__label">{{ t('product.tvaLabel') }}</label>
        <div class="epp__dd" @click.stop>
          <button
            type="button"
            class="epp__dd-trigger"
            :class="{ 'epp__dd-trigger--active': !!tvaId.value }"
            @click="tvaDropdownOpen.value = !tvaDropdownOpen.value"
          >
            <span :class="tvaId.value ? '' : 'epp__dd-placeholder'">{{
              selectedTaxRate
                ? `${selectedTaxRate.name} — ${selectedTaxRate.rate}%`
                : loadingTaxRates
                  ? t('product.tvaLoading')
                  : t('product.tvaPlaceholder')
            }}</span>
            <i
              class="pi pi-chevron-down epp__dd-arrow"
              :class="{ 'epp__dd-arrow--open': tvaDropdownOpen.value }"
            />
          </button>
          <div v-if="tvaDropdownOpen.value" class="epp__dd-panel">
            <div class="epp__dd-search-wrap">
              <i class="pi pi-search" />
              <input
                v-model="tvaSearch.value"
                class="epp__dd-search"
                :placeholder="t('product.tvaSearch')"
                @click.stop
              />
            </div>
            <ul class="epp__dd-list">
              <li
                v-for="tx in filteredTaxRates"
                :key="tx.id"
                class="epp__dd-item"
                :class="{ 'epp__dd-item--active': tvaId.value === tx.id }"
                @click="selectTvaId(tx.id)"
              >
                {{ tx.name }} — {{ tx.rate }}%
              </li>
              <li
                v-if="!loadingTaxRates && filteredTaxRates.length === 0"
                class="epp__dd-empty"
              >
                {{ t('product.tvaNoResult') }}
              </li>
            </ul>
          </div>
        </div>
        <p class="epp__hint">{{ t('product.tvaHint') }}</p>
      </div>
    </div>
  </CollapsibleSection>
</template>

<style scoped>
.epp__fields {
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
}
.epp__field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  min-width: 0;
}
.epp__field-row {
  display: grid;
  gap: 0.875rem;
}
.epp__field-row--2 {
  grid-template-columns: 1fr 1fr;
}
@media (max-width: 640px) {
  .epp__field-row--2 {
    grid-template-columns: 1fr;
  }
}
.epp__label {
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--nxt-on-surface-muted);
  display: flex;
  align-items: center;
  gap: 0.375rem;
}
.epp__req {
  color: var(--nxt-error);
}
.epp__input {
  width: 100%;
  height: 2.625rem;
  padding: 0 0.875rem;
  border: 1.5px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-card-bg);
  font-size: 0.9375rem;
  font-family: inherit;
  color: var(--nxt-on-surface);
  outline: none;
  transition:
    border-color 0.15s,
    background 0.15s;
  box-sizing: border-box;
}
.epp__input:focus {
  border-color: var(--nxt-primary);
}
.epp__input--price {
  padding-right: 2.5rem;
}
.epp__input--error {
  border-color: var(--nxt-error, #dc2626);
}
.epp__input--error:focus {
  border-color: var(--nxt-error, #dc2626);
}
.epp__field-error {
  margin: 0.25rem 0 0;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--nxt-error, #dc2626);
}

/* Price wrap */
.epp__price-wrap {
  position: relative;
}
.epp__price-unit {
  position: absolute;
  right: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--nxt-on-surface-muted);
  pointer-events: none;
}

/* Type chips */
.epp__type-chips {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.epp__type-chip {
  height: 2.25rem;
  padding: 0 1rem;
  border: 1.5px solid var(--nxt-outline-variant);
  border-radius: 9999px;
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface-muted);
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
}
.epp__type-chip:hover {
  border-color: var(--nxt-primary);
  color: var(--nxt-primary);
}
.epp__type-chip--active {
  border-color: var(--nxt-primary);
  background: rgba(0, 0, 0, 0.1);
  color: var(--nxt-primary);
  font-weight: 700;
}

.epp__hint {
  margin: 0.25rem 0 0;
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted, #999);
  line-height: 1.4;
}

/* Dropdown TVA */
.epp__dd {
  position: relative;
}
.epp__dd-trigger {
  width: 100%;
  height: 2.75rem;
  padding: 0 0.875rem;
  border-radius: 0.75rem;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.1));
  background: var(--nxt-surface-low, #f8f9fa);
  color: var(--nxt-on-surface);
  font-size: 0.875rem;
  font-family: inherit;
  outline: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  transition: border-color 0.15s;
  box-sizing: border-box;
}
.epp__dd-trigger:hover {
  border-color: var(--nxt-primary, #141c29);
}
.epp__dd-trigger--active {
  border-color: #0b1320;
  font-weight: 600;
}
[data-theme='dark'] .epp__dd-trigger--active {
  border-color: #fff;
}
.epp__dd-placeholder {
  color: var(--nxt-on-surface-muted, #999);
  font-weight: 400;
}
.epp__dd-arrow {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-variant);
  transition: transform 0.15s;
  flex-shrink: 0;
}
.epp__dd-arrow--open {
  transform: rotate(180deg);
}
.epp__dd-panel {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 200;
  background: var(--nxt-card-bg, #fff);
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.1));
  border-radius: 0.75rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  overflow: hidden;
}
[data-theme='dark'] .epp__dd-panel {
  background: #1e2025;
}
.epp__dd-search-wrap {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  border-bottom: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.08));
  color: var(--nxt-on-surface-variant);
}
.epp__dd-search {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.8125rem;
  font-family: inherit;
  color: var(--nxt-on-surface);
}
.epp__dd-list {
  max-height: 220px;
  overflow-y: auto;
  padding: 0.375rem;
  margin: 0;
  list-style: none;
}
.epp__dd-item {
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface);
  cursor: pointer;
}
.epp__dd-item:hover {
  background: var(--nxt-surface-mid, rgba(0, 0, 0, 0.04));
}
.epp__dd-item--active {
  background: #0b1320;
  color: #fff;
  font-weight: 600;
}
[data-theme='dark'] .epp__dd-item--active {
  background: #fff;
  color: #0b1320;
}
.epp__dd-empty {
  padding: 0.75rem;
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted, #999);
}

/* Select */
.epp__select-wrap {
  position: relative;
}
.epp__select {
  width: 100%;
  height: 2.625rem;
  padding: 0 2.25rem 0 0.875rem;
  border: 1.5px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-card-bg);
  font-size: 0.9375rem;
  font-family: inherit;
  color: var(--nxt-on-surface);
  outline: none;
  appearance: none;
  cursor: pointer;
  transition: border-color 0.15s;
}
.epp__select:focus {
  border-color: var(--nxt-primary);
}
.epp__select-icon {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.625rem;
  color: var(--nxt-on-surface-muted);
  pointer-events: none;
}

[data-theme='dark'] .epp__input,
[data-theme='dark'] .epp__select {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: #fff;
}
[data-theme='dark'] .epp__input:focus,
[data-theme='dark'] .epp__select:focus {
  border-color: #ffd60a;
  background: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .epp__type-chip {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.6);
}
[data-theme='dark'] .epp__type-chip:hover {
  border-color: #ffd60a;
  color: #ffd60a;
}
[data-theme='dark'] .epp__type-chip--active {
  border-color: #ffd60a;
  background: rgba(255, 214, 10, 0.12);
  color: #ffd60a;
}
</style>
